<template>
  <v-container grid-list-lg class="pa-0" fluid>
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="resetAndLoad"
    />
    <v-card :loading="loading && customers.length === 0" outlined>
      <ListTitle
        :access="checkRightStatus(7)"
        :color="$style.purchase.titlebar"
        :title="`${customers.length}${totalCount ? ' / ' + totalCount : ''} Customer`"
        @add="add"
      />
      <v-card-title>
        <v-flex xs12>
          <v-text-field
            v-model="search"
            label="Search"
            outlined
            autofocus
            prepend-inner-icon="search"
            rounded
            dense
            single-line
            hide-details
            clearable
          />
        </v-flex>
      </v-card-title>

      <v-card-text class="touch-view-container">
        <v-data-table
          :headers="headers"
          :items="customers"
          :loading="loadingMore"
          :sort-desc="sortDesc"
          :sort-by="sortBy"
          hide-default-footer
          :items-per-page="customers.length"  
        >
          <template v-slot:item="{ item }">
            <tr class="pointer" @click="viewProfile(item)">
              <td class="text-left">
                <div class="font-weight-bold" v-html="$highlight(item.name, (search || '').toLowerCase())">
                  {{ item.name }}
                </div>
                <div v-html="$highlight(item.cellNumber, (search || '').toLowerCase())">
                  {{ item.cellNumber }}
                </div>
              </td>
              <td>
                <v-tooltip bottom v-if="item.blockOrder">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="red" dark v-bind="attrs" v-on="on">mdi-cancel</v-icon>
                  </template>
                  <span>Ordering is blocked</span>
                </v-tooltip>
              </td>
              <td class="text-right">
                <span v-if="checkRightStatus(35)">{{ item.balance | currency }}</span>
              </td>
            </tr>
          </template>

          <!-- empty state when nothing at all -->
          <v-alert
            slot="no-results"
            :value="!loading && customers.length === 0"
            color="error"
            icon="warning"
          >
            Your search for "{{ search }}" found no results.
          </v-alert>
        </v-data-table>

        <!-- loader row (shows while fetching more) -->
        <div v-if="loadingMore" class="d-flex align-center justify-center py-4">
          <v-progress-circular indeterminate />
          <span class="ml-3">Loading more…</span>
        </div>

        <!-- infinite scroll sentinel -->
        <div
          v-intersect="onIntersect"
          style="height: 1px;"
        ></div>

        <!-- reached end -->
        <div v-if="done && customers.length > 0" class="text-center caption grey--text pb-3">
          No more customers
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import customerService from "../service.js";
const Create = () => import("./Create");

export default {
  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedCustomer: null,

      // table state
      headers: [
        { text: "Business Name", value: "name",     sortable: true,  align: "left" },
        { text: "",              value: "cellNumber", sortable: false, align: "left" },
        { text: this.$t("labels.balance"), value: "balance", width: "30%", align: "left", sortable: true },
      ],
      sortBy: "balance",
      sortDesc: true,

      // infinite scroll state
      customers: [],
      totalCount: 0,
      limit: 60,          // how many per page (you asked for 50/60; set to 60 here)
      offset: 0,
      loading: true,      // initial page spinner on the card
      loadingMore: false, // spinner when loading next chunks
      done: false,        // no more pages

      // search
      search: "",
      // totals (kept from original; optional)
      total: 0,
      fullTotal: 0,
    };
  },
  async created() {
    this.resetAndLoad();
    this.$events.listen("CustomerMiniList", () => this.resetAndLoad());
  },
  beforeDestroy() {
    this.$events.remove("CustomerMiniList");
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
  },
  components: {
    Create,
  },
  watch: {
    // when the user clears or types a new query, start over
    search: {
      handler(val) {
        // simple debounce
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => {
          this.resetAndLoad();
        }, 300);
      },
      immediate: false,
    },
  },
  methods: {
    ...mapActions("customer", ["setCustomer"]),

    updateTotal(val) {
      this.total = 0;
      val.forEach((row) => (this.total += row.balance));
    },

    viewProfile(val) {
      this.setCustomer(val.id);
    },

    add() {
      this.createDialog = true;
    },

    // Reset list and load the first page
    async resetAndLoad() {
      this.loading = true;
      this.loadingMore = false;
      this.done = false;
      this.offset = 0;
      this.customers = [];
      this.totalCount = 0;
      await this.loadMore();
      this.loading = false;
    },

    // Called automatically by the sentinel when it enters viewport
    async onIntersect(entries) {
      const entry = entries[0];
      if (!entry || this.loading || this.loadingMore || this.done) return;
      if (entry.isIntersecting) {
        await this.loadMore();
      }
    },

    // Fetch the next chunk and append
    async loadMore() {
      // guard
      if (this.loadingMore || this.done) return;

      this.loadingMore = true;
      try {
        const where = {
          limit: this.limit,
          offset: this.offset,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        };

        // sales rep scoping
        if (!this.currentUser.isAdmin && this.checkRightStatus(50)) {
          where.SalesRepUserId = this.currentUser.id;
        }
        // server-side search
        if (this.search && this.search.trim().length > 0) {
          where.search = this.search.trim();
        }

        const res = await customerService.getAll(where);

        // Your API commonly responds as { count, data }
        const count = Number(res.count || 0);
        const rows  = Array.isArray(res.data) ? res.data : [];

        // append
        this.customers = this.customers.concat(rows);
        this.totalCount = count;

        // update running totals (optional)
        rows.forEach(r => (this.fullTotal += r.balance || 0));

        // prepare next page
        this.offset += this.limit;

        // stop if we reached the end
        if (this.customers.length >= count || rows.length === 0) {
          this.done = true;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Customer infinite load error:", err);
        // fail-safe to avoid spin
        this.done = true;
      } finally {
        this.loadingMore = false;
      }
    },

    editCustomer(customer) {
      this.selectedCustomer = customer;
      this.editDialog = true;
    },

    deleteCustomer(customer) {
      if (!this.checkRightStatus(39)) {
        this.$swal(
          "Delete Customer",
          "You are not allow to delete customer with balance.",
          "warning"
        );
        return;
      }
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.value) {
          customerService
            .delete(customer.id)
            .then((response) => {
              if (response.data) {
                this.$swal("Deleted!", "Customer has been deleted.", "success");
                this.resetAndLoad();
              }
            })
            .catch((error) => {
              throw error;
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.touch-view-container {
  height: calc(100vh - 300px);
  width: 100%;
  overflow: auto;
}
</style>